"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import dynamic from "next/dynamic";
import styles from "./PlayerThemesSection.module.css";

// The real embed player, mounted once and re-styled live per preset. Same
// component the embed customizer uses — swapping controlTheme/controlOptions/
// playerTheme props never re-mounts it or re-fetches the video.
const VideoPlayerEmbedPreviewNoSSR = dynamic(
  () => import("@/app/components/VideoPlayer/VideoPlayerEmbedPreview.js"),
  { ssr: false, loading: () => null },
);

// ---------------------------------------------------------------------------
// Demo video — the same composite render the section showed before. Only the
// progressive MP4 exists for this id (no HLS playlist), so we force the MP4
// path with `source: "mp4"` and hand the player the full.mp4 directly.
// ---------------------------------------------------------------------------
const DEMO_VIDEO_ID = "c50ec940-5241-4323-849e-039ec2186d55";
const DEMO_VIDEO_URL = `https://videos.flare.link/${DEMO_VIDEO_ID}/composite/full.mp4`;
const DEMO_POSTER_URL = `https://videos.flare.link/${DEMO_VIDEO_ID}/thumbnail-hq.jpg`;
const DEMO_DURATION = 203.417;
const DEMO_VIDEO_INFO = {
  id: DEMO_VIDEO_ID,
  source: "mp4", // explicit MP4 → stays off the (non-existent) HLS path
  duration: DEMO_DURATION,
  width: 1280,
  height: 720,
};

// Two chapters, only for the Blue clay preset — purely to show off the
// chapter UI on the timeline + chapters menu. Shape: { id, startTime, title }.
const BLUE_CLAY_CHAPTERS = [
  { id: "ch-intro", startTime: 0, title: "Intro" },
  { id: "ch-walkthrough", startTime: 118, title: "Walkthrough" },
];

// Looped-thumbnail (freeze) state: the player autoplays a short muted loop and
// freezes the UI as paused — the same Vidalytics-style state real loop embeds
// use. This is what surfaces the click-to-unmute overlays and the play-overlay
// speed selector, so the presets showcase those features.
const LOOP_START_SEC = 0;
const LOOP_END_SEC = 8;

/* Inline stroke icons (lucide-style) — one per preset. */
const ICONS = {
  bold: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path
        d="M13 2 4 13h7l-1 9 9-12h-7l1-8z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  ),
  vsl: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m10 9 5 3-5 3z" fill="currentColor" stroke="none" />
    </svg>
  ),
  clay: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m12 3 9 5-9 5-9-5 9-5z" />
      <path d="m3 13 9 5 9-5" />
    </svg>
  ),
  minimal: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 16h18" />
      <circle cx="6.5" cy="16" r="0.4" fill="currentColor" stroke="none" />
    </svg>
  ),
};

// ---------------------------------------------------------------------------
// Presets — hardcoded from Sander@coet.org's prod embed presets, cleaned for a
// marketing showcase: resume-play / password-gate dropped, subtitles + chapters
// turned off (no data) except Blue clay which gets two demo chapters.
// ---------------------------------------------------------------------------
const PRESETS = [
  {
    id: "vsl",
    label: "High-CTR VSL",
    // "High CTR VSL" — flare, green, large pulsing click-to-unmute, no speed,
    // seeking disabled.
    settings: {
      playerTheme: "flare",
      embedBorderRadius: 5,
      controlTheme: {
        textColor: "#ffffff",
        progressFillColor: "#ffffff",
        progressThumbColor: "#ffffff",
        progressTrackColor: "rgba(255,255,255,0.3)",
        hoverBackgroundColor: "rgba(0,0,0,0.12)",
        containerBackgroundColor: "rgba(1, 185, 22, 1)",
      },
      controlOptions: {
        showSpeed: false,
        showVolume: true,
        showChapters: false,
        showTimeline: true,
        showSubtitles: false,
        showTimestamp: true,
        showFullscreen: true,
        showPlayButton: true,
        showSkipBack10: true,
        showBigPlayButton: false,
        showClickToUnmute: true,
        showSkipForward10: false,
        showSpeedSelector: false,
        largeClickToUnmute: true,
        pulseClickToUnmute: true,
        flareTimelineHeight: 10,
        disableTimelineSeeking: true,
        showOverlaySpeedSelector: false,
      },
    },
  },
  {
    id: "bold",
    label: "Bold",
    // "Barbie With Speed" — clay, pink, overlay speed selector.
    // Timeline removed per request (showTimeline: false).
    settings: {
      playerTheme: "clay",
      embedBorderRadius: 5,
      controlTheme: {
        textColor: "#ffffff",
        progressFillColor: "#ffffff",
        progressThumbColor: "#ffffff",
        progressTrackColor: "rgba(255,255,255,0.3)",
        hoverBackgroundColor: "rgba(0,0,0,0.12)",
        containerBackgroundColor: "rgba(233, 65, 150, 1)",
      },
      controlOptions: {
        showSpeed: true,
        showVolume: true,
        showChapters: false,
        showTimeline: false,
        showSubtitles: false,
        showTimestamp: true,
        showFullscreen: true,
        showPlayButton: true,
        showSkipBack10: true,
        showBigPlayButton: true,
        showClickToUnmute: true,
        showSkipForward10: false,
        showSpeedSelector: false,
        largeClickToUnmute: false,
        pulseClickToUnmute: false,
        flareTimelineHeight: 10,
        disableTimelineSeeking: false,
        showOverlaySpeedSelector: true,
      },
    },
  },

  {
    id: "clay",
    label: "Blue clay",
    // "Blue Clay" — clay, blue. Two demo chapters enabled to show the UI.
    chapters: BLUE_CLAY_CHAPTERS,
    settings: {
      playerTheme: "clay",
      embedBorderRadius: 5,
      controlTheme: {
        textColor: "#ffffff",
        progressFillColor: "#ffffff",
        progressThumbColor: "#ffffff",
        progressTrackColor: "rgba(255,255,255,0.3)",
        hoverBackgroundColor: "rgba(0,0,0,0.12)",
        containerBackgroundColor: "rgba(56, 0, 255, 0.8786574442675159)",
      },
      controlOptions: {
        showSpeed: true,
        showVolume: true,
        showChapters: true,
        showTimeline: true,
        showSubtitles: false,
        showTimestamp: true,
        showFullscreen: true,
        showPlayButton: true,
        showSkipBack10: true,
        showBigPlayButton: true,
        showClickToUnmute: false,
        showSkipForward10: false,
        showSpeedSelector: false,
        largeClickToUnmute: false,
        pulseClickToUnmute: true,
        flareTimelineHeight: 10,
        disableTimelineSeeking: false,
        showOverlaySpeedSelector: false,
      },
    },
  },
  {
    id: "minimal",
    label: "Minimal",
    // "Simple With Unmute" — simple theme, orange. Shows both the click-to-unmute
    // box and the play-overlay speed selector (they coexist, like Bold).
    settings: {
      playerTheme: "simple",
      embedBorderRadius: 5,
      controlTheme: {
        textColor: "#ffffff",
        progressFillColor: "#ffffff",
        progressThumbColor: "#ffffff",
        progressTrackColor: "rgba(255,255,255,0.3)",
        hoverBackgroundColor: "rgba(0,0,0,0.12)",
        containerBackgroundColor: "rgba(255, 103, 2, 1.0)",
      },
      controlOptions: {
        showSpeed: true,
        showVolume: true,
        showChapters: false,
        showTimeline: true,
        showSubtitles: false,
        showTimestamp: true,
        showFullscreen: true,
        showPlayButton: true,
        showSkipBack10: true,
        showBigPlayButton: true,
        showClickToUnmute: true,
        showSkipForward10: false,
        showSpeedSelector: true,
        largeClickToUnmute: false,
        pulseClickToUnmute: false,
        flareTimelineHeight: 10,
        disableTimelineSeeking: false,
        showOverlaySpeedSelector: true,
      },
    },
  },
];

export default function PlayerThemesSection() {
  const [active, setActive] = useState(PRESETS[0].id);
  const activePreset = PRESETS.find((p) => p.id === active) || PRESETS[0];
  const s = activePreset.settings;

  // Mount the (heavy) real player only once the section nears the viewport.
  const previewRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setMounted(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setMounted(true);
          obs.disconnect();
        }
      },
      { rootMargin: "400px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Default the showcase playback to 1.2× — matches the overlay speed selector's
  // pre-selected 1.2×, so the preview actually plays at the speed it advertises.
  // (The player only applies that speed on a real play-button click, which the
  // autoplay loop bypasses, so we set the element's playbackRate directly.)
  const handlePlayerReady = useCallback((api) => {
    try {
      const video = api?.getVideoElement?.();
      if (!video) return;
      video.playbackRate = 1.2;
      // Re-assert if the browser resets the rate when media data loads.
      const reapply = () => {
        video.playbackRate = 1.2;
      };
      video.addEventListener("loadeddata", reapply, { once: true });
    } catch {
      /* non-fatal: playback just stays at 1× */
    }
  }, []);

  // Sliding highlight: one background that animates to sit behind the active
  // button. We measure the active button's box relative to the tablist and
  // drive an absolutely-positioned indicator. `animate` stays false for the
  // first measurement so the indicator doesn't slide in from x=0 on mount.
  const tablistRef = useRef(null);
  const btnRefs = useRef({});
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const [animate, setAnimate] = useState(false);

  useLayoutEffect(() => {
    const list = tablistRef.current;
    const el = btnRefs.current[active];
    if (!list || !el) return;
    const lr = list.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    setIndicator({ left: er.left - lr.left, width: er.width });
  }, [active]);

  // Recompute on resize and once webfonts settle (label widths shift).
  useEffect(() => {
    function measure() {
      const list = tablistRef.current;
      const el = btnRefs.current[active];
      if (!list || !el) return;
      const lr = list.getBoundingClientRect();
      const er = el.getBoundingClientRect();
      setIndicator({ left: er.left - lr.left, width: er.width });
    }
    window.addEventListener("resize", measure);
    if (typeof document !== "undefined" && document.fonts?.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }
    return () => window.removeEventListener("resize", measure);
  }, [active]);

  // Enable the slide transition only after the indicator has been placed once.
  useEffect(() => {
    const id = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.header}>
          <p className={styles.subtitle}>
            A few examples of how you can customize the player to your
            video.
          </p>

          <p className={styles.mobileSubtitle}>
            See a few examples of customized players.
          </p>
          
          <h2 className={styles.title}>
            Design your own player - see a few examples
          </h2>

         
        </div>

        {/* Wrapper lets the elevation bloom extend beyond the panel edges
            without being clipped by the panel's own overflow. */}
        <div className={styles.cardWrap}>
          <div className={styles.card}>
            <div
              ref={tablistRef}
              className={styles.tabs}
              role="tablist"
              aria-label="Player presets"
            >
              <span
                className={styles.tabIndicator}
                style={{
                  transform: `translateX(${indicator.left}px)`,
                  width: `${indicator.width}px`,
                  transition: animate ? undefined : "none",
                }}
                aria-hidden="true"
              />
              {PRESETS.map((p) => {
                const isActive = p.id === active;
                return (
                  <button
                    key={p.id}
                    ref={(el) => {
                      btnRefs.current[p.id] = el;
                    }}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
                    onClick={() => setActive(p.id)}
                  >
                    <span className={styles.tabIcon} aria-hidden="true">
                      {ICONS[p.id]}
                    </span>
                    <span className={styles.tabLabel}>{p.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Single real player; the active preset drives its appearance
                props live. Poster sits behind it until the player mounts. */}
            <div
              ref={previewRef}
              className={styles.preview}
              style={{
                backgroundImage: `url('${DEMO_POSTER_URL}')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              {mounted && (
                <VideoPlayerEmbedPreviewNoSSR
                  url={DEMO_VIDEO_URL}
                  posterUrl={DEMO_POSTER_URL}
                  videoInfo={DEMO_VIDEO_INFO}
                  initialDuration={DEMO_DURATION}
                  initialDimensions={{
                    width: DEMO_VIDEO_INFO.width,
                    height: DEMO_VIDEO_INFO.height,
                  }}
                  controlTheme={s.controlTheme}
                  controlOptions={s.controlOptions}
                  playerTheme={s.playerTheme}
                  bufferProfile="minimal"
                  embedBorderRadiusPx={s.embedBorderRadius}
                  embedBorderRadiusCss={`${s.embedBorderRadius}px`}
                  chapters={activePreset.chapters}
                  loopOverlayConfig={{
                    enabled: true,
                    startTime: LOOP_START_SEC,
                    endTime: LOOP_END_SEC,
                  }}
                  onPlayerReady={handlePlayerReady}
                  unboundedHeight
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
